import type {
  CustomNode,
  NodeStruct,
  RepeatBodySlot,
} from '@/types/node-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import {
  isRepeatAttemptEligible,
  isRepeatJudgeEligible,
  isRepeatNode,
  parseRepeatFeedbackHandle,
  getRepeatPseudoCycleConfig,
  REPEAT_MAX_ITERATIONS_HARD,
} from './repeat-control';
import {
  branchEdgesLeavingRegion,
  checkBranchesRejoin,
} from './region-branches';

/**
 * The loop as the canvas shows it, derived from the graph the same way the
 * backend compiler (`temporal/repeat_control/pseudo_cycle.py`) derives it:
 *
 *   entry ──…──▶ evaluator ──(condition edge)──▶ Repeat Until
 *     ▲                                              │
 *     └────────────(repeat edge, feedback)───────────┘
 *
 * The region is every node on a forward path from the entry to the
 * evaluator. This module answers "which loop is this node in", checks the
 * compiler's rules live (in plain words, before Prepare) and reads a
 * finished run back into something a person can follow.
 */

export interface RepeatLoop {
  carrierId: string;
  carrier: CustomNode;
  /** The node the back edge points at (the step that repeats). */
  entryId: string | null;
  /** The node whose output feeds the Repeat Until's condition input. */
  evaluatorId: string | null;
  /** Every node on a forward path from entry to evaluator (entry and evaluator included). */
  regionIds: string[];
  conditionEdges: CustomEdge[];
  repeatEdges: CustomEdge[];
  /** The slot on the entry node that receives the judge's feedback. */
  feedbackSlot: RepeatBodySlot | null;
}

export type RuleSeverity = 'error' | 'warning';

export interface LoopRuleProblem {
  /** Stable code; mirrors the backend's RepeatContractError code where one exists. */
  code: string;
  severity: RuleSeverity;
  /** One sentence a person can act on. */
  message: string;
  /** What to change. */
  hint?: string;
  /** Nodes to highlight on the canvas. */
  nodeIds: string[];
}

const EXECUTION_EDGE_TYPES = new Set<string>([
  ConnectionType.PORT_TO_PORT,
  ConnectionType.PORT_TO_PARAM,
  ConnectionType.CONDITIONAL,
]);
const NON_EXECUTABLE_NODE_TYPES = new Set(['tool', 'memory', 'annotation']);

export const isRepeatEdge = (edge: Pick<CustomEdge, 'edge_type'>): boolean =>
  edge.edge_type === ConnectionType.REPEAT;

const struct = (node: CustomNode | undefined): NodeStruct | undefined =>
  node?.data?.struct;

export const nodeLabel = (node: CustomNode | undefined): string => {
  const s = struct(node);
  return (
    s?.user_config_name ||
    (node?.data as { display_name?: string } | undefined)?.display_name ||
    s?.display_name ||
    'a node'
  );
};

const isExecutionNode = (node: CustomNode): boolean => {
  const s = struct(node);
  if (!s) return false;
  return !NON_EXECUTABLE_NODE_TYPES.has(String(s.node_type));
};

const edgeSource = (edge: CustomEdge): string =>
  edge.source_node_id || edge.source;
const edgeTarget = (edge: CustomEdge): string =>
  edge.target_node_id || edge.target;

/** Forward adjacency over ordinary execution edges (repeat edges excluded). */
const forwardAdjacency = (
  edges: readonly CustomEdge[],
  executionIds: Set<string>,
): { forward: Map<string, Set<string>>; reverse: Map<string, Set<string>> } => {
  const forward = new Map<string, Set<string>>();
  const reverse = new Map<string, Set<string>>();
  for (const edge of edges) {
    if (isRepeatEdge(edge)) continue;
    if (!EXECUTION_EDGE_TYPES.has(String(edge.edge_type))) continue;
    const from = edgeSource(edge);
    const to = edgeTarget(edge);
    if (!executionIds.has(from) || !executionIds.has(to)) continue;
    if (!forward.has(from)) forward.set(from, new Set());
    forward.get(from)!.add(to);
    if (!reverse.has(to)) reverse.set(to, new Set());
    reverse.get(to)!.add(from);
  }
  return { forward, reverse };
};

const reachable = (start: string, adjacency: Map<string, Set<string>>) => {
  const seen = new Set<string>();
  const queue = [start];
  while (queue.length) {
    const current = queue.shift()!;
    if (seen.has(current)) continue;
    seen.add(current);
    for (const next of adjacency.get(current) ?? []) {
      if (!seen.has(next)) queue.push(next);
    }
  }
  return seen;
};

/** Kahn levels over a node subset; null when the subset has a cycle. */
export const topologicalLevels = (
  ids: Set<string>,
  edges: readonly CustomEdge[],
): string[][] | null => {
  const adjacency = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  for (const id of ids) inDegree.set(id, 0);
  for (const edge of edges) {
    if (isRepeatEdge(edge)) continue;
    if (!EXECUTION_EDGE_TYPES.has(String(edge.edge_type))) continue;
    const from = edgeSource(edge);
    const to = edgeTarget(edge);
    if (!ids.has(from) || !ids.has(to)) continue;
    if (!adjacency.has(from)) adjacency.set(from, []);
    adjacency.get(from)!.push(to);
    inDegree.set(to, (inDegree.get(to) ?? 0) + 1);
  }
  const levels: string[][] = [];
  let current = [...ids].filter((id) => inDegree.get(id) === 0).sort();
  let placed = 0;
  while (current.length) {
    levels.push(current);
    placed += current.length;
    const following: string[] = [];
    for (const id of current) {
      for (const to of (adjacency.get(id) ?? []).sort()) {
        inDegree.set(to, inDegree.get(to)! - 1);
        if (inDegree.get(to) === 0) following.push(to);
      }
    }
    current = following.sort();
  }
  return placed === ids.size ? levels : null;
};

/** The slot an edge lands on, as the backend compiler resolves it. */
export const edgeTargetSlot = (
  edge: CustomEdge,
  target: CustomNode | undefined,
): RepeatBodySlot | null => {
  const fromHandle = parseRepeatFeedbackHandle(edge.targetHandle);
  if (fromHandle) return fromHandle;
  if (edge.target_param_id) {
    return { kind: 'param', target_id: edge.target_param_id };
  }
  const portId = edge.target_port_id || edge.targetHandle || '';
  const s = struct(target);
  const port = s?.input_ports?.find((candidate) => candidate.id === portId);
  if (port) return { kind: 'input', target_id: port.name || portId };
  const param = s?.params?.find(
    (candidate) => candidate.bindable_config?.input_port?.id === portId,
  );
  if (param) return { kind: 'param', target_id: param.field_id };
  return portId ? { kind: 'input', target_id: portId } : null;
};

const sameSlot = (a: RepeatBodySlot | null, b: RepeatBodySlot | null) =>
  Boolean(a && b && a.kind === b.kind && a.target_id === b.target_id);

/** Describe the loop around one Repeat Until carrier from the graph alone. */
export const describeLoop = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  carrierId: string,
): RepeatLoop | null => {
  const carrier = nodes.find((node) => node.id === carrierId);
  if (!carrier || !isRepeatNode(struct(carrier))) return null;
  const repeatEdges = edges.filter(
    (edge) => isRepeatEdge(edge) && edgeSource(edge) === carrierId,
  );
  const conditionEdges = edges.filter(
    (edge) =>
      !isRepeatEdge(edge) &&
      edgeTarget(edge) === carrierId &&
      (edge.edge_type === ConnectionType.PORT_TO_PARAM ||
        edge.edge_type === ConnectionType.PORT_TO_PORT),
  );
  const entryId = repeatEdges[0] ? edgeTarget(repeatEdges[0]) : null;
  const evaluatorId = conditionEdges[0] ? edgeSource(conditionEdges[0]) : null;
  const executionIds = new Set(
    nodes.filter(isExecutionNode).map((node) => node.id),
  );
  let regionIds: string[] = [];
  if (entryId && evaluatorId && entryId !== carrierId) {
    const { forward, reverse } = forwardAdjacency(edges, executionIds);
    // Edges into the carrier do not extend the region.
    for (const set of forward.values()) set.delete(carrierId);
    const descendants = reachable(entryId, forward);
    const ancestors = reachable(evaluatorId, reverse);
    regionIds = [...descendants]
      .filter((id) => ancestors.has(id) && id !== carrierId)
      .sort();
  }
  const entry = entryId ? nodes.find((node) => node.id === entryId) : undefined;
  return {
    carrierId,
    carrier,
    entryId,
    evaluatorId,
    regionIds,
    conditionEdges,
    repeatEdges,
    feedbackSlot: repeatEdges[0] ? edgeTargetSlot(repeatEdges[0], entry) : null,
  };
};

/** Every loop on the canvas, one per Repeat Until node. */
export const describeLoops = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
): RepeatLoop[] =>
  nodes
    .filter((node) => isRepeatNode(struct(node)))
    .map((node) => describeLoop(nodes, edges, node.id))
    .filter((loop): loop is RepeatLoop => loop !== null);

// Every canvas node asks "which loop am I in?" on each render; the answer
// only changes when the node or edge arrays do, so remember the last one.
let loopsCache: {
  nodes: readonly CustomNode[];
  edges: readonly CustomEdge[];
  loops: RepeatLoop[];
} | null = null;

export const describeLoopsCached = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
): RepeatLoop[] => {
  if (loopsCache && loopsCache.nodes === nodes && loopsCache.edges === edges) {
    return loopsCache.loops;
  }
  const loops = describeLoops(nodes, edges);
  loopsCache = { nodes, edges, loops };
  return loops;
};

/** The loop a node belongs to (as entry, evaluator or intermediate), if any. */
export const loopContaining = (
  loops: readonly RepeatLoop[],
  nodeId: string,
): RepeatLoop | null =>
  loops.find((loop) => loop.regionIds.includes(nodeId)) ?? null;

export type RegionRole = 'entry' | 'evaluator' | 'step';

export const regionRole = (
  loop: RepeatLoop,
  nodeId: string,
): RegionRole | null => {
  if (!loop.regionIds.includes(nodeId)) return null;
  if (nodeId === loop.entryId) return 'entry';
  if (nodeId === loop.evaluatorId) return 'evaluator';
  return 'step';
};

// --- live rule checks -------------------------------------------------------

const problem = (
  code: string,
  message: string,
  nodeIds: string[] = [],
  hint?: string,
  severity: RuleSeverity = 'error',
): LoopRuleProblem => ({ code, severity, message, hint, nodeIds });

/** The catalog's copy of a node's struct (eligibility flags can be stale on a saved node). */
export const catalogStructFor = (
  s: NodeStruct | undefined,
  catalog: readonly NodeStruct[] | undefined,
): NodeStruct | undefined =>
  (s && catalog?.find((entry) => entry.node_id === s.node_id)) ?? s;

export const canBeRepeated = (
  s: NodeStruct | undefined,
  catalog?: readonly NodeStruct[],
): boolean => {
  const effective = catalogStructFor(s, catalog);
  return Boolean(effective && isRepeatAttemptEligible(effective));
};

export const canJudge = (
  s: NodeStruct | undefined,
  catalog?: readonly NodeStruct[],
): boolean => {
  const effective = catalogStructFor(s, catalog);
  return Boolean(effective && isRepeatJudgeEligible(effective));
};

const eligibilityAlternatives = (
  role: 'attempt' | 'judge',
  catalog: readonly NodeStruct[] | undefined,
): string => {
  const names = (catalog ?? [])
    .filter((entry) =>
      role === 'attempt'
        ? isRepeatAttemptEligible(entry)
        : isRepeatJudgeEligible(entry),
    )
    .map((entry) => entry.display_name)
    .filter(Boolean);
  const unique = [...new Set(names)].sort();
  if (unique.length === 0) return '';
  return `Nodes that can ${role === 'attempt' ? 'be repeated' : 'judge'}: ${unique.join(', ')}.`;
};

/**
 * Check one loop against the backend compiler's rules, in plain words.
 *
 * Mirrors `compile_repeat_pseudo_cycle` in pseudo_cycle.py; each problem's
 * `code` is the backend's RepeatContractError code so the two can be
 * compared in tests.
 */
export const checkLoopRules = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  loop: RepeatLoop,
  catalog?: readonly NodeStruct[],
): LoopRuleProblem[] => {
  const problems: LoopRuleProblem[] = [];
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const name = (id: string | null) => nodeLabel(id ? byId.get(id) : undefined);
  const carrierName = name(loop.carrierId);

  // One loop per workflow.
  const allRepeatEdges = edges.filter(isRepeatEdge);
  if (allRepeatEdges.length > 1) {
    problems.push(
      problem(
        'repeat_multiple_regions',
        'A workflow can have one loop today; this canvas has more than one repeat edge.',
        [...new Set(allRepeatEdges.map(edgeSource))],
        'Remove the extra Repeat Until node and its repeat edge, or split the work into two workflows.',
      ),
    );
  }

  // Authored policy.
  const config = getRepeatPseudoCycleConfig(struct(loop.carrier));
  const ceiling = Math.min(
    struct(loop.carrier)?.repeat_runtime_limits?.max_iterations ??
      REPEAT_MAX_ITERATIONS_HARD,
    REPEAT_MAX_ITERATIONS_HARD,
  );
  if (!config) {
    problems.push(
      problem(
        'repeat_config_missing',
        `${carrierName} has no loop settings yet.`,
        [loop.carrierId],
        'Pick the step to repeat and the judge in the panel; the settings are written for you.',
      ),
    );
  } else {
    if (
      !Number.isInteger(config.max_iterations) ||
      config.max_iterations < 1 ||
      config.max_iterations > ceiling
    ) {
      problems.push(
        problem(
          'repeat_max_iterations_exceeds_platform',
          `The attempt limit must be between 1 and ${ceiling}.`,
          [loop.carrierId],
          `Set "at most" to a number from 1 to ${ceiling}.`,
        ),
      );
    }
    if (
      config.region != null ||
      config.runtime_limits != null ||
      (config.feedback_bindings?.length ?? 0) > 0
    ) {
      problems.push(
        problem(
          'repeat_authored_runtime_state',
          'The loop settings contain server-compiled state that must not be saved.',
          [loop.carrierId],
          'Re-save the loop from the panel; it writes only the authored part.',
        ),
      );
    }
  }

  // The two edges that define the loop.
  if (loop.repeatEdges.length === 0) {
    problems.push(
      problem(
        'repeat_edge_missing',
        `${carrierName} does not say which step to repeat.`,
        [loop.carrierId],
        'Pick the step in the panel, or drag from the "repeat" handle back to the step\u2019s feedback input.',
      ),
    );
  } else if (loop.repeatEdges.length > 1) {
    problems.push(
      problem(
        'repeat_multiple_regions',
        `${carrierName} has more than one repeat edge.`,
        [loop.carrierId],
        'Keep one repeat edge.',
      ),
    );
  }
  if (loop.conditionEdges.length === 0) {
    problems.push(
      problem(
        'repeat_condition_edge_ambiguous',
        `${carrierName} has no judge connected to its Evaluator input.`,
        [loop.carrierId],
        'Pick the judge in the panel (a Validate or Classify node after the step), or connect its output to the Evaluator input.',
      ),
    );
  } else if (loop.conditionEdges.length > 1) {
    problems.push(
      problem(
        'repeat_condition_edge_ambiguous',
        `${carrierName} has ${loop.conditionEdges.length} nodes connected to its Evaluator input; it needs exactly one judge.`,
        [loop.carrierId, ...loop.conditionEdges.map(edgeSource)],
        'Disconnect all but the judge.',
      ),
    );
  }
  if (!loop.entryId || !loop.evaluatorId) return problems;

  const entry = byId.get(loop.entryId);
  const evaluator = byId.get(loop.evaluatorId);
  if (!entry || loop.entryId === loop.carrierId) {
    problems.push(
      problem(
        'repeat_edge_target_invalid',
        'The repeat edge must point back at a step upstream of the judge.',
        [loop.carrierId],
      ),
    );
    return problems;
  }
  if (!evaluator || loop.evaluatorId === loop.carrierId) {
    problems.push(
      problem(
        'repeat_evaluator_missing',
        'The Evaluator input must come from a node that judges the step\u2019s output.',
        [loop.carrierId],
      ),
    );
    return problems;
  }

  // Removing the repeat edge must leave a DAG.
  const executionIds = new Set(
    nodes.filter(isExecutionNode).map((node) => node.id),
  );
  if (topologicalLevels(executionIds, edges) === null) {
    problems.push(
      problem(
        'repeat_unbounded_cycle',
        'The canvas still has a cycle that is not the repeat edge.',
        [],
        'Only the dashed repeat edge may go backwards; remove any other edge that closes a loop.',
      ),
    );
    return problems;
  }

  // Region shape.
  if (
    !loop.regionIds.includes(loop.entryId) ||
    !loop.regionIds.includes(loop.evaluatorId)
  ) {
    problems.push(
      problem(
        'repeat_region_path_missing',
        `${name(loop.entryId)} does not lead to ${name(loop.evaluatorId)}.`,
        [loop.entryId, loop.evaluatorId],
        'The judge must read the step\u2019s output: connect the step (directly or through other nodes) into the judge.',
      ),
    );
    return problems;
  }
  const regionSet = new Set(loop.regionIds);
  const levels = topologicalLevels(regionSet, edges) ?? [];
  if (
    levels.length === 0 ||
    levels[0].length !== 1 ||
    levels[0][0] !== loop.entryId
  ) {
    const extras = (levels[0] ?? []).filter((id) => id !== loop.entryId);
    problems.push(
      problem(
        'repeat_region_entry_ambiguous',
        extras.length
          ? `The loop has more than one starting node: ${[loop.entryId, ...extras].map(name).join(', ')}.`
          : 'The loop must start at exactly one node.',
        [loop.entryId, ...extras],
        'Every node inside the loop must come after the repeated step; move the others outside the loop or feed them from the step.',
      ),
    );
  }
  const last = levels[levels.length - 1] ?? [];
  if (last.length !== 1 || last[0] !== loop.evaluatorId) {
    const extras = last.filter((id) => id !== loop.evaluatorId);
    problems.push(
      problem(
        'repeat_region_evaluator_ambiguous',
        extras.length
          ? `The loop has more than one final node: ${[loop.evaluatorId, ...extras].map(name).join(', ')}.`
          : 'The loop must end at exactly one judge.',
        [loop.evaluatorId, ...extras],
        'Everything inside the loop must flow into the judge.',
      ),
    );
  }

  // External edges.
  const ordinary = edges.filter(
    (edge) =>
      !isRepeatEdge(edge) &&
      EXECUTION_EDGE_TYPES.has(String(edge.edge_type)) &&
      executionIds.has(edgeSource(edge)) &&
      executionIds.has(edgeTarget(edge)),
  );
  const externalIncoming: CustomEdge[] = [];
  // Branches of a Switch / If that leave the loop get the rejoin sentence below.
  const leavingBranches = branchEdgesLeavingRegion(edges, regionSet, byId);
  for (const edge of ordinary) {
    if (leavingBranches.has(edge.id)) continue;
    const from = edgeSource(edge);
    const to = edgeTarget(edge);
    if (regionSet.has(to) && !regionSet.has(from)) {
      if (to !== loop.entryId) {
        problems.push(
          problem(
            'repeat_region_external_input_ambiguous',
            `${name(from)} feeds ${name(to)}, which is inside the loop but is not the repeated step.`,
            [from, to],
            `Outside values may enter the loop only through ${name(loop.entryId)}. Connect ${name(from)} to it instead, or move ${name(to)} out of the loop.`,
          ),
        );
      } else {
        externalIncoming.push(edge);
      }
    }
    if (regionSet.has(from) && !regionSet.has(to) && to !== loop.carrierId) {
      problems.push(
        problem(
          'repeat_region_external_output_ambiguous',
          `${name(from)} is inside the loop but sends its output to ${name(to)} outside it.`,
          [from, to],
          `Only the judge may leave the loop, through the Evaluator edge. Read the loop\u2019s result from ${carrierName} (Final value) instead.`,
        ),
      );
    }
  }
  for (const edge of edges) {
    if (isRepeatEdge(edge)) continue;
    const from = edgeSource(edge);
    const to = edgeTarget(edge);
    if (regionSet.has(to) && !executionIds.has(from) && byId.has(from)) {
      problems.push(
        problem(
          'repeat_region_handles_unsupported',
          `${name(to)} is inside the loop and has ${name(from)} attached; tools, memory and notes cannot be used inside a loop yet.`,
          [from, to],
          'Detach it, or move the node out of the loop.',
        ),
      );
    }
  }

  // Every region node must be safe for its role.
  for (const id of loop.regionIds) {
    const node = byId.get(id);
    const s = struct(node);
    if (!s) continue;
    const role: 'attempt' | 'judge' =
      id === loop.entryId
        ? 'attempt'
        : id === loop.evaluatorId
          ? 'judge'
          : canJudge(s, catalog)
            ? 'judge'
            : 'attempt';
    const ok =
      role === 'attempt' ? canBeRepeated(s, catalog) : canJudge(s, catalog);
    if (ok) continue;
    const kind = s.display_name || s.node_id;
    const why =
      s.node_id === 'switch_node'
        ? 'A Switch cannot be inside a loop today; branch inside the step\u2019s code instead, or put the Switch after the loop.'
        : s.node_id === 'if_node'
          ? 'An If cannot be inside a loop in this deployment; branch inside the step\u2019s formula instead, or put the If after the loop.'
          : role === 'judge'
            ? `A ${kind} cannot judge a loop. ${eligibilityAlternatives('judge', catalog)}`
            : `A ${kind} cannot be repeated. ${eligibilityAlternatives('attempt', catalog)}`;
    problems.push(
      problem(
        'repeat_region_node_unsafe',
        `${name(id)} (${kind}) cannot be inside the loop as the ${
          id === loop.entryId
            ? 'repeated step'
            : id === loop.evaluatorId
              ? 'judge'
              : 'in-between step'
        }.`,
        [id],
        why.trim(),
      ),
    );
  }

  // Phase 8d: a Switch / If inside the loop must rejoin before the judge.
  problems.push(
    ...checkBranchesRejoin({
      kind: 'repeat',
      nodes,
      edges,
      regionIds: loop.regionIds,
      entryId: loop.entryId,
      exitId: loop.evaluatorId,
      name,
    }),
  );

  // The judge's subject must come from inside the loop, once.
  const evaluatorStruct = catalogStructFor(struct(evaluator), catalog);
  const subjectSlots =
    evaluatorStruct?.repeat_body_contract?.subject_slots ?? [];
  const subjectEdges = ordinary.filter(
    (edge) =>
      edgeTarget(edge) === loop.evaluatorId &&
      regionSet.has(edgeSource(edge)) &&
      subjectSlots.some((slot) =>
        sameSlot(slot, edgeTargetSlot(edge, evaluator)),
      ),
  );
  if (canJudge(evaluatorStruct, catalog) && subjectEdges.length !== 1) {
    problems.push(
      problem(
        'repeat_evaluator_subject_ambiguous',
        subjectEdges.length === 0
          ? `${name(loop.evaluatorId)} is not reading the step\u2019s output.`
          : `${name(loop.evaluatorId)} has ${subjectEdges.length} values connected to the input it judges; it needs exactly one.`,
        [loop.evaluatorId],
        subjectEdges.length === 0
          ? `Connect the output of ${name(loop.entryId)} (or the node before the judge) to the judge\u2019s ${subjectSlots[0]?.target_id ?? 'value'} input.`
          : 'Keep one connection into the judged input.',
      ),
    );
  }

  // Feedback must land on a declared feedback slot and not collide.
  const entryStruct = catalogStructFor(struct(entry), catalog);
  const feedbackSlots = entryStruct?.repeat_body_contract?.feedback_slots ?? [];
  if (loop.repeatEdges[0] && canBeRepeated(entryStruct, catalog)) {
    const target = loop.feedbackSlot;
    if (!target || !feedbackSlots.some((slot) => sameSlot(slot, target))) {
      problems.push(
        problem(
          'repeat_feedback_target_invalid',
          `The repeat edge lands on ${target ? `"${target.target_id}"` : 'an input'} of ${name(loop.entryId)}, which cannot take feedback.`,
          [loop.entryId],
          feedbackSlots.length
            ? `Connect it to ${feedbackSlots.map((slot) => `"${slot.target_id}"`).join(' or ')} instead.`
            : undefined,
        ),
      );
    } else {
      const collision = externalIncoming.find((edge) =>
        sameSlot(edgeTargetSlot(edge, entry), target),
      );
      if (collision) {
        problems.push(
          problem(
            'repeat_feedback_external_collision',
            `"${target.target_id}" on ${name(loop.entryId)} receives both the judge\u2019s feedback and a value from ${name(edgeSource(collision))}.`,
            [loop.entryId, edgeSource(collision)],
            `The first attempt reads "${target.target_id}" from the value typed on the node; later attempts receive the feedback. Remove the edge from ${name(edgeSource(collision))}, or connect it to a different input.`,
          ),
        );
      }
    }
  }
  const seenExternal = new Map<string, string>();
  for (const edge of externalIncoming) {
    const slot = edgeTargetSlot(edge, entry);
    if (!slot) continue;
    const key = `${slot.kind}:${slot.target_id}`;
    const previous = seenExternal.get(key);
    if (previous) {
      problems.push(
        problem(
          'repeat_external_fanin_ambiguous',
          `"${slot.target_id}" on ${name(loop.entryId)} has two outside sources (${name(previous)} and ${name(edgeSource(edge))}).`,
          [loop.entryId, previous, edgeSource(edge)],
          'Keep one source per input.',
        ),
      );
    }
    seenExternal.set(key, edgeSource(edge));
  }

  // Exit condition and feedback path must read the judge.
  if (config) {
    const referencesJudge = JSON.stringify(config.exit_condition).includes(
      '["judge"',
    );
    if (!referencesJudge) {
      problems.push(
        problem(
          'repeat_exit_condition_no_judge',
          'The stop condition does not read anything from the judge.',
          [loop.carrierId],
          'Build the condition from the judge\u2019s outputs (for Validate: "is valid" is true).',
        ),
      );
    }
    if (
      config.feedback_source_path[0] !== 'judge' ||
      config.feedback_source_path.length < 2
    ) {
      problems.push(
        problem(
          'repeat_feedback_source_invalid',
          'The feedback must be one of the judge\u2019s outputs.',
          [loop.carrierId],
          'Pick which judge output goes back into the step (for Validate: "value").',
        ),
      );
    }
  }

  return problems;
};
